import { Entity, HexColor, Language } from '@nx-smartmonkey/shared/domain';
import { removeEmpty } from '@nx-smartmonkey/shared/helpers';
import { SupervisorEntityField } from './SupervisorEntityField';
import {
  SupervisorStopCanceledType,
  SupervisorStopCompletedType,
  SupervisorStopFieldType,
  SupervisorStopSurveyType,
} from './SupervisorFields';

export interface SupervisorUpdateProps {
  email?: string;
  short_id?: string;
  name?: string;
  company?: string;
  phone?: string;
  color?: HexColor;
  language?: Language;
  stop_fields?: Array<SupervisorEntityField<SupervisorStopFieldType>>;
  stop_completed_fields?: Array<SupervisorEntityField<SupervisorStopCompletedType>>;
  stop_canceled_fields?: Array<SupervisorEntityField<SupervisorStopCanceledType>>;
  survey_fields?: Array<SupervisorEntityField<SupervisorStopSurveyType>>;
}

export interface SupervisorProps {
  project_id: string;
  organization_id: string;
  email: string;
  short_id: string;
  name: string;
  company: string;
  phone: string;
  color?: HexColor;
  language?: Language;

  stop_fields: Array<SupervisorEntityField<SupervisorStopFieldType>>;
  stop_completed_fields: Array<SupervisorEntityField<SupervisorStopCompletedType>>;
  stop_canceled_fields: Array<SupervisorEntityField<SupervisorStopCanceledType>>;
  survey_fields: Array<SupervisorEntityField<SupervisorStopSurveyType>>;

  // CRUD
  created_by?: string;
  created_at?: Date;
  updated_at?: Date;
}

export class Supervisor extends Entity<SupervisorProps> {
  get id(): string {
    return this._id!;
  }
  get project_id(): string {
    return this.props.project_id;
  }
  get organization_id(): string {
    return this.props.organization_id;
  }
  get email(): string {
    return this.props.email;
  }
  get short_id(): string {
    return this.props.short_id;
  }
  get name(): string {
    return this.props.name;
  }
  get company(): string {
    return this.props.company;
  }
  get phone(): string {
    return this.props.phone;
  }
  get color(): HexColor | undefined {
    return this.props.color;
  }
  get language(): Language | undefined {
    return this.props.language;
  }
  get stop_fields(): Array<SupervisorEntityField<SupervisorStopFieldType>> {
    return this.props.stop_fields;
  }
  get stop_completed_fields(): Array<SupervisorEntityField<SupervisorStopCompletedType>> {
    return this.props.stop_completed_fields;
  }
  get stop_canceled_fields(): Array<SupervisorEntityField<SupervisorStopCanceledType>> {
    return this.props.stop_canceled_fields;
  }
  get survey_fields(): Array<SupervisorEntityField<SupervisorStopSurveyType>> {
    return this.props.survey_fields;
  }
  get created_at(): Date | undefined {
    return this.props.created_at;
  }
  get created_by(): string | undefined {
    return this.props.created_by;
  }
  get updated_at(): Date | undefined {
    return this.props.updated_at;
  }
  update(props: SupervisorUpdateProps) {
    removeEmpty(props);

    if (props.email !== undefined) this.props.email = props.email;
    if (props.short_id !== undefined) this.props.short_id = props.short_id;
    if (props.name !== undefined) this.props.name = props.name;
    if (props.company !== undefined) this.props.company = props.company;
    if (props.phone !== undefined) this.props.phone = props.phone;
    if (props.color !== undefined) this.props.color = props.color;
    if (props.language !== undefined) this.props.language = props.language;
    if (props.stop_fields !== undefined) this.props.stop_fields = props.stop_fields;
    if (props.stop_completed_fields !== undefined) this.props.stop_completed_fields = props.stop_completed_fields;
    if (props.stop_canceled_fields !== undefined) this.props.stop_canceled_fields = props.stop_canceled_fields;
    if (props.survey_fields !== undefined) this.props.survey_fields = props.survey_fields;

    this.props.updated_at = new Date();
  }
  private constructor(props: SupervisorProps, id?: string) {
    super(props, id);
  }

  static create(props: SupervisorProps, id?: string): Supervisor {
    return new Supervisor(props, id);
  }

  /**
   * Creates the admin link for inspection.
   * @param {supervisorId} - Project supervisor id.
   * @return {string} - Admin link.
   */
  getAsSupervisorLink(supervisorsDomain: string, token: string): string {
    const path = `admin?&token=${encodeURIComponent(token)}&as_supervisor=${this.id}`;
    const adminLink = `${supervisorsDomain}/${path}`;
    return adminLink;
  }
}
